import { IImage } from '@models/IImage';
import BreakpointEnum from '@models/enums/BreakpointEnum';
import breakpoints from '@utility/config/breakpoints';
import useBreakpoint from '@utility/hooks/useBreakpoint';
import useDevicePixelRatio from '@utility/hooks/useDevicePixelRatio';
import classNames from 'classnames';
import {
  PropsWithChildren,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';

interface Props {
  readonly alternativeText?: string;
  readonly aspectRatio?: number;
  readonly classes?: string;
  readonly containerRef?: RefObject<HTMLElement | null>;
  readonly image: IImage;
  readonly imageClasses?: string;
  readonly lazyLoad?: boolean;
  readonly quality?: number;
  readonly sizes?: string;
  readonly height?: number;
  readonly width?: number;
  readonly onLoad?: () => void;
}

const Image = ({
  alternativeText,
  aspectRatio, // width / height e.g. 0.5, 2 etc.
  children,
  classes,
  containerRef,
  image,
  imageClasses,
  lazyLoad = true,
  quality = 70,
  sizes = '',
  height,
  width,
  onLoad,
}: PropsWithChildren<Props>) => {
  const devicePixelRatio = useDevicePixelRatio() ?? 1;
  const breakpoint = useBreakpoint() ?? BreakpointEnum.XLarge;
  const imgRef = useRef<HTMLImageElement>(null);

  const {
    url,
    altText,
    originalHeight,
    originalWidth,
    imageFocalPoint,
    srcSet: source,
  } = image ?? {};
  const fileExtension = url?.substring(url?.lastIndexOf('.') + 1);
  const hasParams = url?.includes('&'); // For images not coming from Opti
  const containerWidth = containerRef?.current?.clientWidth;

  const breakpointWidths = [
    375 * devicePixelRatio,
    752 * devicePixelRatio,
    1024 * devicePixelRatio,
    1408 * devicePixelRatio,
    1600 * devicePixelRatio,
  ];

  const calculatedAspectRatio = useMemo(() => {
    if (aspectRatio) return aspectRatio;

    if (originalWidth && originalHeight) return originalWidth / originalHeight;
  }, [aspectRatio, originalWidth, originalHeight]);

  const generateUrlWithExtensions = useCallback(
    (width?: number) => {
      const qParam = quality ? `&quality=${quality}` : '';
      const fpParam = imageFocalPoint
        ? `&rxy=${imageFocalPoint.replace('|', ',')}`
        : '';

      if (!width) {
        return `${url}?format=webp$${fpParam}${qParam}`;
      }

      const wParam = `&width=${width}`;
      const h = aspectRatio ? width / aspectRatio : height;
      const hParam = h ? `&height=${h}` : '';

      return `${url}?format=webp${wParam}${hParam}${fpParam}${qParam}`;
    },
    [url, quality, calculatedAspectRatio, imageFocalPoint]
  );

  const srcSet = useMemo(() => {
    return source
      ? `${url} ${breakpointWidths[1]}w, ${source} ${breakpointWidths[2]}w`
      : breakpointWidths
          .map(
            (bpWidth, i) =>
              `${generateUrlWithExtensions(
                i < breakpointWidths.length - 1 ? bpWidth : 1920
              )} ${bpWidth}w`
          )
          .join(', ');
  }, [source, breakpointWidths, generateUrlWithExtensions]);

  useEffect(() => {
    // Fallback - sometimes onLoad does not trigger if the image is cached
    if (imgRef?.current?.complete && onLoad) onLoad();
  }, [imgRef]);

  if (!url || devicePixelRatio == null) return null;

  if (fileExtension === 'svg' || isValidBase64ImageSource(url)) {
    return (
      <img
        src={url}
        alt={alternativeText || altText || 'image'}
        className={imageClasses}
        draggable="false"
      />
    );
  }

  return (
    <>
      <picture className={classNames(classes, 'tw-block tw-overflow-hidden')}>
        {!width && !containerWidth && !hasParams && (
          <source type="image/webp" sizes={sizes} srcSet={srcSet} />
        )}
        <img
          ref={imgRef}
          alt={alternativeText || altText || 'image'}
          sizes={sizes}
          className={imageClasses}
          draggable="false"
          style={{
            aspectRatio: calculatedAspectRatio,
          }}
          loading={lazyLoad ? 'lazy' : 'eager'}
          fetchPriority={lazyLoad ? 'auto' : 'high'}
          onLoad={onLoad}
          src={
            hasParams || source
              ? url
              : generateUrlWithExtensions(
                  width ?? containerWidth ?? breakpoints[breakpoint]
                )
          }
        />
        {children}
      </picture>
    </>
  );
};

const isValidBase64ImageSource = (imageSrc: string) => {
  const regex = /^data:image\/(png|jpg|jpeg|gif);base64,[A-Za-z0-9+/]+={0,2}$/;
  return regex.test(imageSrc);
};

export default Image;
